import AdUnit from '../../core/AdUnit';
import getVPAIDAd from './viaVPAID';

import {
  event,
  AdUnitStatus,
  MediaType,
  AdvIDPrefix,
  PlayerType,
  ACTIVE_UNIT_STATUSES,
  TagsGroup
} from '../../utils/const';
import { getRandomString, createVideoSlot } from '../../utils/utils';
import { get } from '../../utils/parseVast';
import { API } from '../../utils/api';
import { PageObserverEvents } from "../../utils/PageObserver";

const BETWEEN_VPAID_DOMAIN = 'cache.betweendigital.com'; // uncommon module behavior for VPAIDs on this domain
const KINOSTREAM_VPAID_DOMAIN = 'kinostream.media';// 'kinostream.media/vpaid/vp.embed.1.0.js';
const SECURE_VPAID_DOMAIN = 'static.skwstat.ru';
const ALWAYS_SECURE_VPAID = 'ds.serving-sys.ru/BurstingCachedScripts/VPAID/HTML5_1_40_0_0/VPAIDAPI.js';

const CHECK_ALIVE_TIMEOUT = 5500;
// const CHECK_VOLUME_TIMEOUT = 250;
const QUARTILE_SAFE_ADDITIONAL_DURATION = 300;

class VPAID extends AdUnit {
  constructor(cfg) {
    super(cfg);

    this.advID = AdvIDPrefix.VPAID + getRandomString();
    this.vpaid = null;
    this.adParameters = null;
    this.iframe = null;
    this._specialBehavior = false;
    this._secureMode = false;
    this._stuckSuspect = false;
    this.pausedByPlayer = false;
    this._quartileApproxDuration = 0;

    this._init();
  }

  _init() {
    const { ad, media, clickThruNode } = this.config;

    if (this._mediaFilesContainsType(media, MediaType.JS)) {
      this.logger.log(`[VPAID - ${this.config.params.tid}] parsing JS media`);
      const vpaidMedia = media[0];
      const adParametersNode = get(ad, 'AdParameters')[0];
      this.adParameters = adParametersNode?.textContent || "{}";

      this._secureMode = vpaidMedia.url.includes(ALWAYS_SECURE_VPAID) || vpaidMedia.url.includes(SECURE_VPAID_DOMAIN) || this.config.params.vpaid === 1; // params.vpaid === 1;
      this.logger.log(`[VPAID - ${this.config.params.tid}] secureMode = ${this._secureMode}`);

      // this.videoSlot = this._secureMode ? undefined : createVideoSlot();
      this.videoSlot = createVideoSlot();
      this.videoSlot.className = 'vads-video-slot';
      
      this.iframe = document.createElement('iframe');
      this.iframe.setAttribute('allowfullscreen', '');
      this.iframe.setAttribute('mozallowfullscreen', '');
      this.iframe.setAttribute('webkitallowfullscreen', '');
      this.iframe.setAttribute('frameborder', '0');
      this.iframe.setAttribute('allow', 'autoplay');
      this.iframe.setAttribute('sandbox', 'allow-forms allow-same-origin allow-pointer-lock allow-popups allow-top-navigation allow-scripts');
      this.iframe.style.cssText = this._secureMode ? 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 9999; overflow: hidden;' : 'display: none;';

      this.iframe.onload = () => {
        // const targetSlot = this._secureMode ? this.iframe.contentDocument.body : this.slot;
        // targetSlot.appendChild(this.videoSlot);

        try {
          this.iframe.contentWindow.onVPAIDLoad = () => {
            const { getVPAIDAd } = this.iframe.contentWindow;

            if (getVPAIDAd) {
              this.logger.log(`[VPAID - ${this.config.params.tid}] script correctly loaded`);

              this.vpaidClickThruURL = clickThruNode ? clickThruNode.textContent : '';
              this._specialBehavior = vpaidMedia.url.includes(BETWEEN_VPAID_DOMAIN) || vpaidMedia.url.includes(KINOSTREAM_VPAID_DOMAIN);

              this.iframe.contentDocument.body.style.cssText = 'padding: 0; margin: 0;display: flex; align-items: center; overflow: hidden';

              this._initVPAID({ getAd: getVPAIDAd, adParameters: this.adParameters });
            } else {
              this.logger.log('[VPAID] getVPAIDAd not found');
              this.emitter.emit(event.Player.TagCompleteRequest, { tid: this.config.params.tid, success: false });
            }
          }
        } catch (e) {
        }
        this.iframe.contentWindow.onVPAIDError = () => {
          this.logger.log(`[VPAID - ${this.config.params.tid}] VPAID script load error`);
          this.emitter.emit(event.Player.TagCompleteRequest, { tid: this.config.params.tid, success: false });
          this.localEmitter.emit(event.Ad.AdError, { code: 0 });
        }

        // this.iframe.contentDocument.body.innerHTML += '<style>video.vads-video-slot { position: absolute; max-width: 100%; height: auto; }</style>';
        // const s = this.iframe.contentDocument.createElement('script');
        // s.onload = () => this.iframe.contentWindow.onVPAIDLoad();
        // s.onerror = () => this.iframe.contentWindow.onVPAIDError();
        // s.src = vpaidMedia.url;
        // this.iframe.contentDocument.body.appendChild(s);

        this.iframe.contentDocument.write('<body><style>video.vads-video-slot { position: absolute; max-width: 100%; height: auto; }</style><script onload="window.onVPAIDLoad();" onerror="window.onVPAIDError();" src="' + vpaidMedia.url + '"></scr' + 'ipt></body>');
        this.iframe.contentDocument.close();
      }
      this.iframe.onerror = (e) => {
        this.logger.error(`[VPAID - ${this.config.params.tid}] iframe error`, e);
        this.emitter.emit(event.Player.TagCompleteRequest, { tid: this.config.params.tid, success: false }); }

      this.slot.appendChild(this.iframe);

      const targetSlot = this._secureMode ? this.iframe.contentDocument.body : this.slot;
      targetSlot.appendChild(this.videoSlot);

      // if (this.videoSlot) {
      //   this.slot.appendChild(this.videoSlot);
      // }
    } else {
      this.logger.log('[VPAID] parsing VAST ad');

      this.adParameters = {
        videos: [...media.filter((mediaFile) => mediaFile.mimetype !== MediaType.JS)],
      };

      if (clickThruNode) {
        const clickThru = clickThruNode.textContent;
        this.adParameters = {
          ...this.adParameters,
          overlay: clickThru,
        };
      }

      try {
        this._initVPAID({ getAd: getVPAIDAd, adParameters: JSON.stringify(this.adParameters) });
      } catch (error) {
        this.logger.log('[VPAID] error! ', error);
        this.emitter.emit(event.Player.TagCompleteRequest, { tid: this.config.params.tid, success: false });
        // emitter.emit(event.Ad.AdError, { advID, params, error: `vpaid error: ${error}` });
      }
    }
  }

  _initVPAID({ getAd, adParameters }) {
    this.logger.log(`[VPAID - ${this.config.params.tid}] prepare VPAID to init`);
    this.vpaid = getAd();
    const slot = this._secureMode ? this.iframe.contentDocument.body : this.slot;

    const width = this.slot.clientWidth;
    const height = width / 16 * 9; // this.slot.clientHeight;
    const viewMode = "normal";
    const desiredBitrate = 1000;
    const creativeData = {
      AdParameters: adParameters,
    };
    const environmentVars = {
      slot,
      videoSlot: this.videoSlot,
      // videoSlotCanAutoPlay: true,
    };

    this.vpaid.handshakeVersion('2.0');

    this.vpaid.subscribe(this._onVPAIDStarted, event.Ad.AdStarted);
    this.vpaid.subscribe(this._onVPAIDVideoStart, event.Ad.AdVideoStart);
    this.vpaid.subscribe(this._onVPAIDImpression, event.Ad.AdImpression);
    this.vpaid.subscribe(this._onVPAIDFirstQuartile, event.Ad.AdVideoFirstQuartile);
    this.vpaid.subscribe(this._onVPAIDMidpoint, event.Ad.AdVideoMidpoint);
    this.vpaid.subscribe(this._onVPAIDThirdQuartile, event.Ad.AdVideoThirdQuartile);
    this.vpaid.subscribe(this._onVPAIDLoaded, event.Ad.AdLoaded);
    this.vpaid.subscribe(this._onVPAIDComplete, event.Ad.AdVideoComplete);
    this.vpaid.subscribe(this._onVPAIDStopped, event.Ad.AdStopped);
    this.vpaid.subscribe(this._onVPAIDDurationChange, event.Ad.AdDurationChange)
    this.vpaid.subscribe(this._onVPAIDError, event.Ad.AdError);
    this.vpaid.subscribe(this._onVPAIDUserClose, event.Ad.AdUserClose);
    this.vpaid.subscribe(this._onVPAIDClickThru, event.Ad.AdClickThru);
    this.vpaid.subscribe(this._onVPAIDPaused, event.Ad.AdPaused);
    this.vpaid.subscribe(this._onVPAIDResumed, event.Ad.AdPlaying);
    this.vpaid.subscribe(this._onVPAIDVolumeChange, event.Ad.AdVolumeChange);

    if (this._specialBehavior) {
      this.emitter.emit(event.Player.AdPreloaded, this);
      this.status = AdUnitStatus.LOADED;
    } else {
      this.timers.initVPAID = setTimeout(() => {
        this.logger.log(`[VPAID - ${this.config.params.tid}] initVPAID timeout (${this.advID}) `);
        this.emitter.emit(event.Player.TagCompleteRequest, { tid: this.config.params.tid, success: false });
        this.localEmitter.emit(event.Ad.AdError, { code: 4 });
      }, this.config.timeouts.initVPAID || 10000);
      this.queueManager.queue[TagsGroup.IN_PROCESS].push(this);

      this.logger.log(`[VPAID - ${this.config.params.tid}] init VPAID`);
      this.vpaid.initAd(width, height, viewMode, desiredBitrate, creativeData, environmentVars);
    }
  }

  _mediaFilesContainsType(mediaFiles, type) {
    return Boolean(mediaFiles.find((mediaFile) => mediaFile.mimetype === type));
  }

  _onVolumeChange = (evt) => {
    const { target } = evt;

    this.logger.log(`[VPAID - ${this.config.params.tid}] attempt to volume change detected. volume: ${target.volume}, muted: ${this.muted}`,);
    if (target.volume > 0 && this.muted) {
      target.volume = 0;
    }
  }

  _createVolumeChangeListeners(container) {
    const muteAllVideos = (videos) => {
      videos.forEach(video => { video.volume = 0; video.addEventListener('volumechange', this._onVolumeChange); });
    }

    const innerVideos = [...container.querySelectorAll('video')];
    const innerFrames = [...container.querySelectorAll('iframe')];

    muteAllVideos(innerVideos);
    innerFrames.forEach(frame => {
      try {
        this._createVolumeChangeListeners(frame.contentDocument.body);
      } catch (e) {}
    });
  }

  // _checkVPAIDVolume = () => {
  //   try {
  //     const volume = this.getVolume();
  //
  //     if (volume > 0 && this.muted) {
  //       this.logger.log(`[VPAID - ${this.config.params.tid}] VPAID volume is ${volume}, but expected 0. Try to mute it...`);
  //       this.setVolume(0);
  //     }
  //
  //     this.timers.checkVolume = setTimeout(() => this._checkVPAIDVolume(), CHECK_VOLUME_TIMEOUT);
  //   } catch (e) {
  //     this.logger.log(`[VPAID - ${this.config.params.tid}] caught error while trying to mute: `, e);
  //   }
  // }

  checkIFrameAlive = () => {
    if (this.status !== AdUnitStatus.PLAYING) return;

    if (!this.iframe.contentDocument) {
      this.logger.log('[VPAID] incorrect iframe.contentDocument value ', this.iframe.contentDocument);
      return this.localEmitter.emit(event.Ad.AdError, { code: 10 });
    }
  }

  _checkVPAIDAlive = () => { // TODO: check behavior when window is blured
    if (this.status !== AdUnitStatus.PLAYING || !this._stuckSuspect) {
      this.timers.checkAlive = setTimeout(() => this._checkVPAIDAlive(), CHECK_ALIVE_TIMEOUT);
      
      return;
    }
    
    this.logger.log('[VPAID] _checkVPAIDAlive', this);

    if (this.vpaid.getAdRemainingTime) {
      const remainingTime = this.vpaid.getAdRemainingTime();

      if (!remainingTime) {
        this.logger.log(`[VPAID - ${this.config.params.tid}] getAdRemainingTime exists, but return invalid value`, remainingTime);

        this.timers.invalidRemainingTimeError = setTimeout(() => this.localEmitter.emit(event.Ad.AdError, { code: 6 }), 2000);
        return;
      }

      if (!this.lastRemainingTime) {
        this.lastRemainingTime = remainingTime;
        return this.timers.checkAlive = setTimeout(() => this._checkVPAIDAlive(), CHECK_ALIVE_TIMEOUT);
      }

      if (this.lastRemainingTime <= remainingTime || !remainingTime) {
        this.logger.log(`[VPAID - ${this.config.params.tid}] seems like vpaid got stuck. Finishing it...`, this.lastRemainingTime, remainingTime);

        try {
          if (this.vpaid.stopAd) this.vpaid.stopAd();
        } catch (e) {
          this.logger.log(`[VPAID - ${this.config.params.tid}] failed stopAd call`);
        }

        this.localEmitter.emit(event.Ad.AdError, { code: 7 });
      } else {
        this.logger.log('[VPAID] VPAID working well', this.lastRemainingTime, remainingTime);

        this.lastRemainingTime = remainingTime;
        return this.timers.checkAlive = setTimeout(() => this._checkVPAIDAlive(), CHECK_ALIVE_TIMEOUT);
      }
    }
  }

  _onVPAIDVolumeChange = () => {
    const volume = this.getVolume();

    if (this.muted && volume > 0) {
      this.logger.log(`[VPAID - ${this.config.params.tid}] _onVPAIDVolumeChange handle attempt to unmute video.`);
      this.setVolume(0);
    } else {
      const { extraEvents } = this.config.playerConfig;

      if (extraEvents && extraEvents.includes('mute') && this.status === AdUnitStatus.PLAYING) {
        this._trackEvent(volume === 0 ? event.Ad.AdVolumeMuteExtra : event.Ad.AdVolumeUnmuteExtra);
      }
    }
  }

  _checkQuartileApproxDuration = () => {
    if (this.status === AdUnitStatus.PLAYING) { // TODO: check unit behaviour during pause
      this.logger.log('_stuckSuspect is true now');
      this._stuckSuspect = true;
    }
  }

  _resetQuartileDurationCheck = () => {
    this._stuckSuspect = false;
    clearTimeout(this.timers.checkQuartileApproxDuration);
    this.timers.checkQuartileApproxDuration = setTimeout(() => this._checkQuartileApproxDuration(), this._quartileApproxDuration + QUARTILE_SAFE_ADDITIONAL_DURATION);
  }

  _onVPAIDClickThru = (...args) => {
    this.localEmitter.emit(event.Ad.AdClickThru);

    setTimeout(() => {
      const paramsURL = args[0];
      const playerHandle = args[2];

      const url = paramsURL || this.vpaidClickThruURL || '';

      if (playerHandle && url && API.isURLValid(url)) {
        window.open(url);
      }
    }, 0);
  }

  _onVPAIDDurationChange = (...args) => {
    this.logger.log(`[VPAID - ${this.config.params.tid}] _onVPAIDDurationChange`, this);
  }

  _onVPAIDLoaded = (...args) => {
    if ((this.status === AdUnitStatus.LOADED && !this._specialBehavior) || this.status === AdUnitStatus.ERROR || this.status === AdUnitStatus.FINISHED) return;

    this.logger.log(`[VPAID - ${this.config.params.tid}] _onVPAIDLoaded`, this);

    if (this._specialBehavior) {
      try {
        setTimeout(() => {
          this.vpaid.setAdVolume(0);
          this.vpaid.startAd();
        }, 100);
      } catch(e) {
        this.logger.log(`[VPAID - ${this.config.params.tid}] caught error when running VPAID with special behaviour: `, e);
        this.localEmitter.emit(event.Ad.AdError, { code: 0 });
      }
    } else {
      if (this.timers.initVPAID) {
        clearTimeout(this.timers.initVPAID);
        this.timers.initVPAID = null;
      }
      
      // const { extraEvents } = this.config.playerConfig;
      // if (extraEvents && extraEvents.includes('err')) {
      //   this._callTrackEventUrl(`https://${this.config.playerConfig.ldom}/event/player?aid=(aid)&sid=(sid)&tid=(tid)&zone=(zone)&event=rtb&event2=err_10&cb=(cb)&n=(views)&tg=(tg)&tu=(tu)`.replace('(aid)', this.config.params.aid).replace('(tid)', this.config.params.tid).replace('(zone)', this.config.playerConfig.zone).replace('(sid)', this.config.playerConfig.sid)) // TODO: test
      // }
      this.emitter.emit(event.Player.AdPreloaded, this);
      this.status = AdUnitStatus.LOADED;
    }
  }

  _onVPAIDStarted = () => {
    if (this.status === AdUnitStatus.ERROR || this.status === AdUnitStatus.FINISHED) return;
    this.logger.log('[VPAID] _onVPAIDStarted', this);
  };

  _onVPAIDVideoStart = () => {
    if (this.status === AdUnitStatus.ERROR || this.status === AdUnitStatus.FINISHED) return;
    if (this.status !== AdUnitStatus.ACTIVE && this.status !== AdUnitStatus.PLAYING && this.config.params.groupId !== TagsGroup.CONTENT) return this.localEmitter.emit(event.Ad.AdError, { code: 8 });
    this.localEmitter.emit(event.Ad.AdVideoStart);
    this.emitter.emit(event.Ad.AdVideoStart, { advID: this.advID, unit: this });

    const duration = this.vpaid.getAdRemainingTime();
    this._quartileApproxDuration = !isNaN(duration) && duration > 0 ? ((this.vpaid.getAdRemainingTime() / 4) * 1000) : 5000;
    // console.log('_quartileApproxDuration: ', this._quartileApproxDuration);
    
    this.slot.classList.add('active');
    
    this.emitter.emit(event.Stream.StartSkipTimer);
    
    if (!this.timers.checkAlive) this.timers.checkAlive = setTimeout(() => this._checkVPAIDAlive(), CHECK_ALIVE_TIMEOUT);
    // if (!this.timers.checkVolume) this.timers.checkVolume = setTimeout(() => this._checkVPAIDVolume(), CHECK_VOLUME_TIMEOUT);
    if (!this.timers.checkQuartileApproxDuration) this.timers.checkQuartileApproxDuration = setTimeout(() => this._checkQuartileApproxDuration(), this._quartileApproxDuration + QUARTILE_SAFE_ADDITIONAL_DURATION);
  }

  _onVPAIDFirstQuartile = () => {
    this.localEmitter.emit(event.Ad.AdVideoFirstQuartile);

    this._resetQuartileDurationCheck();
  }

  _onVPAIDMidpoint = () => {
    this.localEmitter.emit(event.Ad.AdVideoMidpoint);

    this._resetQuartileDurationCheck();
  }

  _onVPAIDThirdQuartile = () => {
    this.localEmitter.emit(event.Ad.AdVideoThirdQuartile);

    this._resetQuartileDurationCheck();
  }

  _onVPAIDImpression = () => {
    if (this.status === AdUnitStatus.ERROR || this.status === AdUnitStatus.FINISHED) return;
    if (this.status !== AdUnitStatus.ACTIVE && this.status !== AdUnitStatus.PLAYING && this.status !== AdUnitStatus.PAUSED && this.config.params.groupId !== TagsGroup.CONTENT) return this.localEmitter.emit(event.Ad.AdError, { code: 8 });
    this.localEmitter.emit(event.Ad.AdImpression);

    this._createVolumeChangeListeners(this.slot);
    this.slot.classList.add('active');

    if (!this.timers.checkAlive) this.timers.checkAlive = setTimeout(() => this._checkVPAIDAlive(), CHECK_ALIVE_TIMEOUT);
    // if (!this.timers.checkVolume) this.timers.checkVolume = setTimeout(() => this._checkVPAIDVolume(), CHECK_VOLUME_TIMEOUT);
  }

  _onVPAIDComplete = (...args) => {
    if (this.status === AdUnitStatus.ERROR || this.status === AdUnitStatus.FINISHED) return;
    // this.localEmitter.emit(event.Ad.AdVideoComplete);
    this._onVPAIDFinished({ success: true });
    this.emitter.emit(event.Ad.AdVideoComplete, { advID: this.advID });
  }

  _onVPAIDStopped = () => {
    if (this.status === AdUnitStatus.ERROR || this.status === AdUnitStatus.FINISHED) return;
    // this.localEmitter.emit(event.Ad.AdVideoComplete, success);
    this._onVPAIDFinished({ success: false });
  }

  _onVPAIDFinished = ({ success }) => {
    this.localEmitter.emit(event.Ad.AdVideoComplete, { success });
  }

  _onVPAIDPaused = () => {
    if (!this.pausedByPlayer) {
      const { pageObserver } = this.impressionDetector;
      if (this.config.playerConfig.type === PlayerType.EMBED && !pageObserver.playerInView) {
        const callback = ({ inView }) => {
          if (inView) {
            this.logger.log('resume ad after player returns in view');
            this.resume();

            pageObserver.unsubscribe(PageObserverEvents.onPlayerInView, callback);
          }
        }

        return pageObserver.subscribe(PageObserverEvents.onPlayerInView, callback);
      }

      this.logger.log('unauthorized pause, resume...');
      this.resume();
    } else {
      this.localEmitter.emit(event.Ad.AdPaused);
    }
    
    this.pausedByPlayer = false;
  }

  _onVPAIDResumed = () => {
    this.localEmitter.emit(event.Ad.AdPlaying);
  }

  _onVPAIDError = (error) => {
    this.logger.log(`[VPAID - ${this.config.params.tid}] error!`, error);
    if (!ACTIVE_UNIT_STATUSES.includes(this.status)) this.emitter.emit(event.Player.TagCompleteRequest, { tid: this.config.params.tid, success: false });

    if (this.status === AdUnitStatus.INIT && this.config.params.groupId === TagsGroup.CONTENT && !this.queueManager.queue[TagsGroup.CONTENT].some(unit => unit.config.params.tid === this.config.params.tid)) {
      this.emitter.emit(event.Player.AdPreloaded, this);
    }
    this.localEmitter.emit(event.Ad.AdError, { code: 5 });
  }

  _onVPAIDUserClose = () => {}

  start() {
    this.logger.log(`[VPAID - ${this.config.params.tid}] start() called`, this);
    
    this._startTimestamp = Date.now();

    this.timers.startAd = setTimeout(() => {
      this.logger.log(`[VPAID - ${this.config.params.tid}] startAd timeout`);
      this.localEmitter.emit(event.Ad.AdError, { code: 2 });
    }, this.config.timeouts.vpaidstart || 5000); // TODO: check behavior when resume ad

    if (this.iframe) this.timers.checkIFrameAlive = setInterval(() => this.checkIFrameAlive(), 2000);

    // this.slot.classList.add('active');

    // this._createVolumeChangeListeners(this.slot);

    if (this.status === AdUnitStatus.PAUSED) {
      this.logger.log('[VPAID] resume VPAID...');
      this.resume();
    } else if (this._specialBehavior) {
      const slot = this._secureMode ? this.iframe.contentDocument.body : this.slot;

      const width = this.slot.clientWidth;
      const height = width / 16 * 9; // this.slot.clientHeight;
      const viewMode = "normal";
      const desiredBitrate = 1000;
      const creativeData = {
        AdParameters: this.adParameters,
      };
      const environmentVars = {
        slot,
        videoSlot: this.videoSlot,
        // videoSlotCanAutoPlay: true,
      };

      this.vpaid.initAd(width, height, viewMode, desiredBitrate, creativeData, environmentVars);
    } else {
      try {
        this.vpaid.setAdVolume(0);
        this.vpaid.startAd();
      } catch (e) {
        this.localEmitter.emit(event.Ad.AdError, { code: 0 });
      }
    }
  }
  // skip = () => { return this.vpaid.skipAd(); }
  resume() { return this.vpaid.resumeAd(); }
  pause() { this.pausedByPlayer = true; return this.vpaid.pauseAd(); }
  getDuration() { return this.vpaid.getAdDuration(); } // TODO: check getAdDuration && getAdRemainingTime for existence before return
  getCurrentTime() { return this.vpaid.getAdDuration() - this.vpaid.getAdRemainingTime(); }
  getVolume() { return this.vpaid.getAdVolume(); }
  setVolume(volume) { this.muted = false; return this.vpaid.setAdVolume(volume); }
  resize = (width, height) => { this.vpaid.resizeAd(width, height, 'normal'); }
}

export default VPAID;
